"use client";

// Sign-in form (client component).
//
// Same `useActionState` pattern as the sign-up form. The error surface is
// intentionally generic — distinguishing "wrong email" vs "wrong password"
// would let an attacker enumerate registered accounts.

import * as React from "react";
import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { Button, Input, Label } from "@aio/platform-ui";
import { signinAction, type AuthActionState } from "../../../auth/actions";

const initialState: AuthActionState = {};

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" loading={pending} className="w-full">
      {pending ? "Signing in..." : "Sign in"}
    </Button>
  );
}

export function SigninForm() {
  const [state, formAction] = useActionState<AuthActionState, FormData>(
    signinAction,
    initialState,
  );

  return (
    <form action={formAction} className="space-y-5" noValidate>
      {state.error ? (
        <div
          role="alert"
          className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger"
        >
          {state.error}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="signin-email">Email</Label>
        <Input
          id="signin-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          aria-invalid={state.fieldErrors?.email ? true : undefined}
          aria-describedby={state.fieldErrors?.email ? "signin-email-error" : undefined}
        />
        {state.fieldErrors?.email ? (
          <p id="signin-email-error" className="text-xs text-danger">
            {state.fieldErrors.email}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signin-password">Password</Label>
        <Input
          id="signin-password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          aria-invalid={state.fieldErrors?.password ? true : undefined}
          aria-describedby={
            state.fieldErrors?.password ? "signin-password-error" : undefined
          }
        />
        {state.fieldErrors?.password ? (
          <p id="signin-password-error" className="text-xs text-danger">
            {state.fieldErrors.password}
          </p>
        ) : null}
      </div>

      <SubmitButton />
    </form>
  );
}
